import { useEffect, useState } from "react";
import { Instagram } from "lucide-react";
import { fetchInstagramPosts } from "@/lib/instagram";
import { trackOutbound } from "@/lib/analytics";

/**
 * Admin-curated Instagram tiles · v2.
 *
 * IMPORTANT (Phase 6.2.1 fix): we used to render <blockquote class="instagram-media">
 * + embed.js so Instagram would inject its own iframe. That broke in Chrome with
 * ERR_BLOCKED_BY_RESPONSE because clicking inside the embed iframe tried to navigate
 * the iframe to instagram.com/<user>/ which IG forbids via X-Frame-Options: DENY.
 *
 * New approach: each tile is a plain <a target="_blank" rel="noopener noreferrer">.
 * No iframes, no embed.js, no X-Frame-Options collisions. The tile is an editorial
 * card with the IG icon, caption preview and a "Ver en Instagram" CTA. Click always
 * opens the post URL in a new tab — guaranteed safe.
 *
 * If the API returns zero posts, the component renders nothing and lets the parent
 * (InstagramBlock) decide whether to show the elegant fallback CTA.
 */
export default function InstagramFeed({ limit = 6 }) {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchInstagramPosts({ featured: true, limit })
      .then((d) => {
        setPosts(Array.isArray(d) ? d : []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [limit]);

  if (loaded && posts.length === 0) {
    // Surface a hint for Mariola without breaking the UX.
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn("[Instagram] Feed vacío — mostrando fallback CTA");
    }
    return null;
  }

  return (
    <div data-testid="instagram-feed" className="relative">
      <div
        className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-6 scroll-smooth"
        style={{ scrollbarWidth: "thin" }}
      >
        {posts.map((p) => (
          <a
            key={p.id}
            href={p.post_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`ig-post-${p.slug}`}
            onClick={() => trackOutbound(p.post_url, { surface: "instagram_carousel" })}
            className="snap-start shrink-0 w-[320px] sm:w-[360px] aspect-square bg-cream-dark rounded-sm overflow-hidden group flex flex-col"
          >
            <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-charcoal to-[#2a2a2a] text-cream group-hover:from-terracotta group-hover:to-[#A64826] transition-colors">
              <div className="text-center px-6">
                <Instagram className="h-9 w-9 mx-auto mb-3 opacity-90" strokeWidth={1.4} />
                <p className="text-[10px] uppercase tracking-[0.28em] text-gold/85 group-hover:text-cream/85">
                  @mujeres_motor
                </p>
              </div>
            </div>
            <div className="p-4 bg-cream-dark/40 border-t border-charcoal/10 min-h-[88px]">
              {p.caption_preview ? (
                <p className="text-sm text-charcoal/80 line-clamp-3 font-serif italic leading-snug">
                  “{p.caption_preview}”
                </p>
              ) : (
                <p className="text-xs uppercase tracking-[0.2em] text-charcoal/55 mt-1">
                  Ver en Instagram →
                </p>
              )}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
